import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { Checkbox } from 'components/shared/Checkbox/Checkbox';
import { CountdownTimer } from 'components/shared/CountdownTimer/CountdownTimer';
import { useProgressTimer } from 'hooks/useProgressTimer';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { ProductGraph } from 'state/product-description/model';
import { getQuoteTradeExecutionWindowInMillis, getQuoteTradeExecutionWindowStartTime } from 'state/quotes/selectors';
import { getAddonManagementFormLastQuoteId } from 'state/selectors';
import type { AppState } from 'state/state.model';
import styled from 'styled-components';

import type { AddonManagementFormProps } from './AddonManagementForm.connect';
import { isNotDynamicColumn, isShowDynamicColumn } from './AddonManagementFormTableHeader';
import { GridCell } from './GridUtils';
import { AddonManagementStatusIcon } from './StatusIcon/AddonManagementStatusIcon';

export const StyledFormCellWrapper = styled.div<{ $isReadOnly: boolean }>`
  font-feature-settings: 'cv01', 'cv10', 'tnum';
  color: ${({ $isReadOnly }) => ($isReadOnly ? '$text-muted' : 'default')};
  pointer-events: ${({ $isReadOnly }) => ($isReadOnly ? 'none' : 'default')};
  opacity: ${({ $isReadOnly }) => ($isReadOnly ? '0.5' : '1')};
  z-index: 1;
`;

const StyledGridCell = styled(GridCell)`
  position: relative;
`;

const StyledCountdownTimer = styled(CountdownTimer)`
  z-index: 1;
`;

const StyledAddonManagementStatusIcon = styled(AddonManagementStatusIcon)`
  .popover-children {
    position: relative;
    z-index: 1;
  }
`;

const StyledFormCellBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

export type AddonManagementDefaultFormProps = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  bgColor: string;
} & Omit<AddonManagementFormProps, 'historicalTrades'>;
export function AddonManagementDefaultForm({
  formId,
  productDescription,
  isUpfrontFeeColumnHidden,
  isOfferPriceColumnHidden,
  isFixedAmountInPercentColumnHidden,
  isTradingPriceColumnHidden,
  isFormSelected,
  status,
  isReadOnly,
  errors,
  warnings,
  validationErrors,
  formJsonRepresentation,
  toggleCheckbox,
  hasTimeoutError,
  hasTechnicalError,
  onMouseEnter,
  onMouseLeave,
  bgColor,
}: AddonManagementDefaultFormProps) {
  const quoteId = useSelector((state: AppState) => getAddonManagementFormLastQuoteId(state, formId));
  const tradeWindowStartTime = useSelector(
    (state: AppState) => getQuoteTradeExecutionWindowStartTime(state, quoteId) ?? -1,
  );
  const tradeWindowInMillis = useSelector(
    (state: AppState) => getQuoteTradeExecutionWindowInMillis(state, quoteId) ?? -1,
  );
  const shouldDisplayCountdown = useMemo(
    () => status === 'Quoted' && tradeWindowStartTime !== -1 && tradeWindowInMillis !== -1,
    [status, tradeWindowStartTime, tradeWindowInMillis],
  );

  const { remainingTimeInSeconds } = useProgressTimer(tradeWindowStartTime, tradeWindowInMillis);

  return (
    <>
      <StyledGridCell
        className="is-sticky-left justify-content-center border-top bg-lvl1 px-1"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="gridcell"
      >
        <StyledFormCellBackground className={bgColor} />
        <Checkbox
          e2e="addon-management-select-checkbox"
          onChange={toggleCheckbox}
          checked={isFormSelected}
          disabled={isReadOnly}
          id={formId}
        />
      </StyledGridCell>
      <StyledGridCell
        className="is-sticky-left-2nd justify-content-center border-top bg-lvl1"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="gridcell"
      >
        <StyledFormCellBackground className={bgColor} />
        {shouldDisplayCountdown ? (
          <StyledCountdownTimer
            compact
            className="d-flex align-items-center justify-content-center"
            color="info"
            iconPosition="BEFORE"
            remainingTimeInSeconds={remainingTimeInSeconds}
          />
        ) : (
          <StyledAddonManagementStatusIcon
            errors={errors}
            warnings={warnings}
            validationErrors={validationErrors}
            formJsonRepresentation={formJsonRepresentation}
            formId={formId}
            status={status}
            hasTimeoutError={hasTimeoutError}
            hasTechnicalError={hasTechnicalError}
          />
        )}
      </StyledGridCell>
      {RenderFormFields(
        productDescription,
        isUpfrontFeeColumnHidden,
        isOfferPriceColumnHidden,
        isFixedAmountInPercentColumnHidden,
        isTradingPriceColumnHidden,
        onMouseEnter,
        onMouseLeave,
        bgColor,
        isReadOnly,
        formId,
      )}
    </>
  );
}

function RenderFormFields(
  productDescription: ProductGraph,
  isUpfrontFeeColumnHidden: boolean,
  isOfferPriceColumnHidden: boolean,
  isFixedAmountInPercentColumnHidden: boolean,
  isTradingPriceColumnHidden: boolean,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
  bgColor: string,
  isReadOnly: boolean,
  formId: string,
) {
  return productDescription.map(blockNode =>
    blockNode.children?.map(
      node =>
        node.type !== 'Technical' &&
        (isShowDynamicColumn(node, 'UpfrontFee', isUpfrontFeeColumnHidden) ||
          isShowDynamicColumn(node, 'OfferPrice', isOfferPriceColumnHidden) ||
          isShowDynamicColumn(node, 'FixedAmountInPercent', isFixedAmountInPercentColumnHidden) ||
          isShowDynamicColumn(node, 'TradingPrice', isTradingPriceColumnHidden) ||
          isNotDynamicColumn(node)) && (
          <StyledGridCell
            key={node.id}
            className="align-middle text-center border-top bg-lvl1 p-1"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            role="gridcell"
          >
            <StyledFormCellBackground className={bgColor} />
            <StyledFormCellWrapper $isReadOnly={isReadOnly} data-e2e={`${node.id}-row`}>
              <RenderFormNodeComponent
                tabId={formId}
                level={1}
                workspaceType="addonManagement"
                quoteWorkspaceVersion="Advanced"
                batchWorkspaceVersion="Advanced"
                node={node}
                mustForceAdvancedMode
              />
            </StyledFormCellWrapper>
          </StyledGridCell>
        ),
    ),
  );
}
